import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@mui/material";
import { Icon } from "@iconify/react";

// Components
import PageWrap from "src/components/PageWrap";
import SiteSettings from "src/components/AutoChange/SiteSettings";
import CurrencyMapping from "src/components/AutoChange/CurrencyMapping";
import SystemSettings from "src/service/SystemSettings";

const AutoChangeSettings = () => {
  const [settingsData, setSettingsData] = useState({
    changerCurrencis: [],
    exchangerCurrencies: [],
  });
  const [settings, setSettings] = useState({
    enabledForSites: [],
    linkedCurrency: [],
    enabled: false,
  });
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [conversionForm, setConversionForm] = useState({ fromCurrency: "", toCurrency: "", amount: "" });
  const [conversionSubmitting, setConversionSubmitting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [conversionError, setConversionError] = useState(null);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ currency: "", amount: "", address: "", extra_id: "", totp: "" });
  const [payoutWalletRegex, setPayoutWalletRegex] = useState(null);
  const [payoutExtraIdRegex, setPayoutExtraIdRegex] = useState(null);

  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [payoutResult, setPayoutResult] = useState(null);
  const [payoutError, setPayoutError] = useState(null);
  const [historyTab, setHistoryTab] = useState(0);
  const [paymentsHistory, setPaymentsHistory] = useState([]);
  const [payoutsHistory, setPayoutsHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadSettings();
    loadBalances();
    loadAllCurrencies();
    loadHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllCurrencies = async () => {
    try {
      const response = await SystemSettings.getAutoChangeCurrencies();
      const currencies = (response.data || []).filter((c) => c.available_for_to_conversion);
      setAllCurrencies(currencies);
    } catch (err) {
      console.error("Ошибка загрузки валют:", err);
    }
  };

  const openConversionModal = (currencyCode) => {
    setConversionForm({ fromCurrency: currencyCode, toCurrency: "", amount: "" });
    setConversionResult(null);
    setConversionError(null);
    setConversionModalOpen(true);
  };

  const closeConversionModal = () => {
    setConversionModalOpen(false);
    setConversionResult(null);
    setConversionError(null);
  };

  const handleConversion = async () => {
    setConversionResult(null);
    setConversionError(null);
    try {
      setConversionSubmitting(true);
      const response = await SystemSettings.createAutoChangeConversion({
        fromCurrency: conversionForm.fromCurrency,
        toCurrency: conversionForm.toCurrency,
        amount: Number(conversionForm.amount),
      });
      setConversionResult(response.data);
    } catch (err) {
      setConversionError(err?.response?.data?.error || "Ошибка конвертации");
    } finally {
      setConversionSubmitting(false);
    }
  };

  const openPayoutModal = (balance) => {
    setPayoutForm({ currency: balance.code, amount: "", address: "", extra_id: "", totp: "" });
    setPayoutWalletRegex(balance.wallet_regex || null);
    setPayoutExtraIdRegex(balance.extra_id_regex || null);
    setPayoutResult(null);
    setPayoutError(null);
    setPayoutModalOpen(true);
  };

  const closePayoutModal = () => {
    setPayoutModalOpen(false);
    setPayoutResult(null);
    setPayoutError(null);
  };

  const handlePayout = async () => {
    setPayoutResult(null);
    setPayoutError(null);

    if (payoutWalletRegex) {
      try {
        const re = new RegExp(payoutWalletRegex);
        if (!re.test(payoutForm.address)) {
          setPayoutError("Неверный формат адреса кошелька");
          return;
        }
      } catch {}
    }

    if (payoutExtraIdRegex && payoutForm.extra_id) {
      try {
        const re = new RegExp(payoutExtraIdRegex);
        if (!re.test(payoutForm.extra_id)) {
          setPayoutError("Неверный формат Memo / Extra ID");
          return;
        }
      } catch {}
    }

    try {
      setPayoutSubmitting(true);
      await SystemSettings.createAutoChangePayout({
        currency: payoutForm.currency,
        amount: Number(payoutForm.amount),
        address: payoutForm.address,
        extra_id: payoutForm.extra_id || undefined,
        totp: payoutForm.totp,
      });
      setPayoutResult("Ордер на вывод запущен");
      loadBalances();
    } catch (err) {
      setPayoutError(err?.response?.data?.error || "Ошибка вывода средств");
    } finally {
      setPayoutSubmitting(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const [paymentsRes, payoutsRes] = await Promise.all([
        SystemSettings.getAutoChangePaymentsHistory(),
        SystemSettings.getAutoChangePayoutsHistory(),
      ]);
      setPaymentsHistory(paymentsRes.data || []);
      setPayoutsHistory(payoutsRes.data || []);
    } catch (err) {
      console.error("Ошибка загрузки истории:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadBalances = async () => {
    try {
      setBalancesLoading(true);
      const response = await SystemSettings.getAutoChangeBalances();
      setBalances(response.data || []);
    } catch (err) {
      console.error("Ошибка загрузки балансов:", err);
    } finally {
      setBalancesLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Загружаем настройки автообмена
      const response = await SystemSettings.getAutoChangeSettings();
      const data = response.data;

      setSettings({
        enabledForSites: data.enabledForSites || [],
        linkedCurrency: data.linkedCurrency || [],
        enabled: data.enabled || false,
      });

      const responseSettingsData =
        await SystemSettings.autoChangeSettingsGetData();
      const settingsData = responseSettingsData.data;

      setSettingsData({
        changerCurrencis: settingsData.changerCurrencis || [],
        exchangerCurrencies: settingsData.exchangerCurrencies || [],
      });
    } catch (err) {
      console.error("Ошибка загрузки настроек автообмена:", err);
      showSnackbar("Ошибка загрузки настроек", "error");
    } finally {
      setLoading(false);
    }
  };

  console.log({ settingsData });

  const PAYMENT_STATUS_LABELS = {
    finished: "Завершён",
    failed: "Ошибка",
    expired: "Истёк",
    partially_paid: "Частично оплачен",
    waiting: "Ожидание",
    confirming: "Подтверждение",
    confirmed: "Подтверждён",
    sending: "Отправка",
    refunded: "Возвращён",
  };

  const PAYOUT_STATUS_LABELS = {
    FINISHED: "Завершён",
    FAILED: "Ошибка",
    REJECTED: "Отклонён",
    SENDING: "Отправка",
    WAITING: "Ожидание",
    PROCESSING: "Обработка",
    CREATED: "Создан",
  };

  const handleDataChange = (newData) => {
    setSettings((prev) => ({ ...prev, ...newData }));
  };

  const handleSwitchChange = (event) => {
    const newValue = event.target.checked;
    setSettings((prev) => ({ ...prev, enabled: newValue }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await SystemSettings.updateAutoChangeSettings(settings);
      showSnackbar("Настройки успешно сохранены", "success");
    } catch (err) {
      console.error("Ошибка сохранения настроек:", err);
      showSnackbar("Ошибка сохранения настроек", err?.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <PageWrap title="Авто Обмен">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </PageWrap>
    );
  }

  return (
    <PageWrap title="Авто Обмен">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Переключатель крипто авто обмена */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled || false}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label="Крипто авто обмен"
          />
        </Box>

        {/* NowPayments */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">NowPayments</Typography>
            <IconButton
              size="small"
              title="Обновить"
              disabled={historyTab === 0 ? balancesLoading : historyLoading}
              onClick={historyTab === 0 ? loadBalances : loadHistory}
            >
              <Icon
                icon={(historyTab === 0 ? balancesLoading : historyLoading) ? "mdi:loading" : "mdi:refresh"}
                width={20} height={20}
                style={(historyTab === 0 ? balancesLoading : historyLoading) ? { animation: "spin 1s linear infinite" } : {}}
              />
            </IconButton>
          </Box>
          <Tabs value={historyTab} onChange={(_, v) => setHistoryTab(v)} sx={{ mb: 2 }}>
            <Tab label="Балансы" />
            <Tab label={`Поступления (${paymentsHistory.length})`} />
            <Tab label={`Выводы (${payoutsHistory.length})`} />
          </Tabs>

          {/* Вкладка: Балансы */}
          {historyTab === 0 && (
            balancesLoading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                    <Skeleton variant="rounded" height={90} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {balances.map((b) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={b.code}>
                    <Card variant="outlined">
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          p: 1.5,
                          "&:last-child": { pb: 1.5 },
                        }}
                      >
                        {b.logo_url ? (
                          <Box
                            component="img"
                            src={b.logo_url}
                            alt={b.code}
                            sx={{ width: 32, height: 32, objectFit: "contain" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              bgcolor: "grey.300",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              fontWeight: "bold",
                            }}
                          >
                            {b.code.slice(0, 3)}
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          title={b.code}
                          noWrap
                          sx={{ maxWidth: "100%" }}
                        >
                          {b.name}
                        </Typography>
                        {b.network && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "100%" }}
                          >
                            {b.network}
                          </Typography>
                        )}
                        <Typography variant="body2" fontWeight="medium">
                          {Number(b.amount).toFixed(4)}
                        </Typography>
                        {b.pendingAmount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            + {Number(b.pendingAmount).toFixed(4)} в ожидании
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                          <IconButton
                            size="small"
                            title="Конвертировать"
                            onClick={() => openConversionModal(b.code)}
                            sx={{ color: "primary.main" }}
                          >
                            <Icon icon="mdi:swap-horizontal" width={18} height={18} />
                          </IconButton>
                          {b.available_for_payout && (
                            <IconButton
                              size="small"
                              title="Вывести средства"
                              onClick={() => openPayoutModal(b)}
                              sx={{ color: "success.main" }}
                            >
                              <Icon icon="mdi:bank-transfer-out" width={18} height={18} />
                            </IconButton>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {balances.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary">Балансы не найдены</Typography>
                  </Grid>
                )}
              </Grid>
            )
          )}

          {/* Вкладка: Поступления */}
          {historyTab === 1 && (
            historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : paymentsHistory.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>Нет данных</Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Валюта</TableCell>
                      <TableCell align="right">Ожидалось</TableCell>
                      <TableCell align="right">Получено</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>ID заказа</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentsHistory.map((p) => (
                      <TableRow key={p.payment_id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {p.created_at ? new Date(p.created_at).toLocaleString("ru-RU") : "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.payment_id}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: "bold" }}>{p.pay_currency?.toUpperCase()}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{p.pay_amount}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{p.actually_paid ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={PAYMENT_STATUS_LABELS[p.payment_status] ?? p.payment_status}
                            size="small"
                            color={
                              p.payment_status === "finished" ? "success" :
                              p.payment_status === "failed" || p.payment_status === "expired" ? "error" :
                              p.payment_status === "partially_paid" ? "warning" :
                              p.payment_status === "waiting" ? "default" : "info"
                            }
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.order_id ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
          )}

          {/* Вкладка: Выводы */}
          {historyTab === 2 && (
            historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : payoutsHistory.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>Нет данных</Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Валюта</TableCell>
                      <TableCell align="right">Сумма</TableCell>
                      <TableCell>Адрес</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payoutsHistory.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                          {p.created_at ? new Date(p.created_at).toLocaleString("ru-RU") : "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.id}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: "bold" }}>{p.currency?.toUpperCase()}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{p.amount}</TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={p.address}>
                          {p.address}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PAYOUT_STATUS_LABELS[p.status] ?? p.status}
                            size="small"
                            color={
                              p.status === "FINISHED" ? "success" :
                              p.status === "FAILED" || p.status === "REJECTED" ? "error" :
                              p.status === "SENDING" ? "primary" : "info"
                            }
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
          )}
        </Box>

        <Divider />

        {/* Настройки по сайтам */}
        <SiteSettings onDataChange={handleDataChange} />

        {/* Сопоставление кодов валют */}
        <CurrencyMapping
          onDataChange={handleDataChange}
          exchangerCurrencies={settingsData.exchangerCurrencies}
          changerCurrencis={settingsData.changerCurrencis}
        />

        {/* Кнопка сохранения */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? "Сохранение..." : "💾 Сохранить настройки"}
          </Button>
        </Box>
      </Box>

      {/* Модалка конвертации */}
      <Dialog open={conversionModalOpen} onClose={closeConversionModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon icon="mdi:swap-horizontal" width={22} height={22} />
            Конвертация валют
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Из валюты</InputLabel>
              <Select
                value={conversionForm.fromCurrency}
                label="Из валюты"
                onChange={(e) => setConversionForm((p) => ({ ...p, fromCurrency: e.target.value }))}
              >
                {balances.map((b) => (
                  <MenuItem key={b.code} value={b.code}>
                    {b.name}&nbsp;
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({Number(b.amount).toFixed(4)})
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Сумма"
              type="number"
              fullWidth
              value={conversionForm.amount}
              onChange={(e) => setConversionForm((p) => ({ ...p, amount: e.target.value }))}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>В валюту</InputLabel>
              <Select
                value={conversionForm.toCurrency}
                label="В валюту"
                onChange={(e) => setConversionForm((p) => ({ ...p, toCurrency: e.target.value }))}
              >
                {allCurrencies.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {conversionResult && (
              <Alert severity="success" onClose={() => setConversionResult(null)}>
                Ордер на конвертацию создан
              </Alert>
            )}
            {conversionError && (
              <Alert severity="error" onClose={() => setConversionError(null)}>
                {conversionError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConversionModal}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleConversion}
            disabled={
              conversionSubmitting ||
              !conversionForm.fromCurrency ||
              !conversionForm.toCurrency ||
              !conversionForm.amount
            }
            startIcon={
              conversionSubmitting
                ? <CircularProgress size={16} />
                : <Icon icon="mdi:swap-horizontal" width={18} height={18} />
            }
          >
            Конвертировать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модалка вывода средств */}
      <Dialog open={payoutModalOpen} onClose={closePayoutModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon icon="mdi:bank-transfer-out" width={22} height={22} />
            Вывод средств — {payoutForm.currency}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Адрес кошелька"
              fullWidth
              value={payoutForm.address}
              onChange={(e) => setPayoutForm((p) => ({ ...p, address: e.target.value }))}
            />
            <TextField
              size="small"
              label="Memo / Destination Tag (если нужно)"
              fullWidth
              value={payoutForm.extra_id}
              onChange={(e) => setPayoutForm((p) => ({ ...p, extra_id: e.target.value }))}
            />
            <TextField
              size="small"
              label="Сумма"
              type="number"
              fullWidth
              value={payoutForm.amount}
              onChange={(e) => setPayoutForm((p) => ({ ...p, amount: e.target.value }))}
            />
            <TextField
              size="small"
              label="TOTP код (6 цифр)"
              fullWidth
              inputProps={{ maxLength: 6, inputMode: "numeric" }}
              value={payoutForm.totp}
              onChange={(e) => setPayoutForm((p) => ({ ...p, totp: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            {payoutResult && (
              <Alert severity="success" onClose={() => setPayoutResult(null)}>
                {payoutResult}
              </Alert>
            )}
            {payoutError && (
              <Alert severity="error" onClose={() => setPayoutError(null)}>
                {payoutError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayoutModal}>Отмена</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handlePayout}
            disabled={
              payoutSubmitting ||
              !payoutForm.address ||
              !payoutForm.amount ||
              payoutForm.totp.length !== 6
            }
            startIcon={
              payoutSubmitting
                ? <CircularProgress size={16} />
                : <Icon icon="mdi:bank-transfer-out" width={18} height={18} />
            }
          >
            Вывести
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageWrap>
  );
};

export default AutoChangeSettings;
